import type { Deal } from "../types";

type FieldType = "num" | "pct" | "bool" | "text" | "select";
interface FieldDef {
  k: keyof Deal;
  label: string;
  type?: FieldType;
  opts?: string[];
  step?: string;
}
interface Group {
  title: string;
  open?: boolean;
  fields: FieldDef[];
}

const GROUPS: Group[] = [
  {
    title: "🏠 Property & units",
    open: true,
    fields: [
      { k: "name", label: "Name", type: "text" },
      { k: "addr", label: "Address", type: "text" },
      { k: "market", label: "Market", type: "text" },
      { k: "stage", label: "Stage", type: "select", opts: ["Prospect", "Screening", "Underwriting", "Offer", "Under Contract", "Closed"] },
      { k: "yearBuilt", label: "Year built" },
      { k: "roofAge", label: "Roof age (yrs)" },
      { k: "flood", label: "Flood zone", type: "select", opts: ["X", "AE", "VE", "AO"] },
      { k: "oz", label: "Opportunity Zone", type: "bool" },
      { k: "zoning", label: "Zoning conforming", type: "bool" },
      { k: "u2c", label: "# of 2BR units" },
      { k: "u2r", label: "2BR rent/mo" },
      { k: "u1c", label: "# of 1BR units" },
      { k: "u1r", label: "1BR rent/mo" },
    ],
  },
  {
    title: "🗺️ Eligibility & designations",
    fields: [
      { k: "censusTract", label: "Census tract (GEOID)", type: "text" },
      { k: "qct", label: "Qualified Census Tract", type: "select", opts: ["unknown", "yes", "no"] },
      { k: "dda", label: "Difficult Development Area", type: "select", opts: ["unknown", "yes", "no"] },
      { k: "usdaRural", label: "USDA rural-eligible", type: "select", opts: ["unknown", "yes", "no"] },
    ],
  },
  {
    title: "🏛️ Title & distress",
    fields: [
      { k: "lisPendens", label: "Pre-foreclosure (lis pendens)", type: "bool" },
      { k: "openLiens", label: "Open liens / judgments ($)" },
    ],
  },
  {
    title: "🧾 Section 8 rent",
    fields: [
      { k: "safmr2", label: "2BR SAFMR" },
      { k: "paymentStd", label: "Payment standard (×FMR)", step: "0.01" },
      { k: "ua", label: "Utility allowance" },
    ],
  },
  {
    title: "💵 Income & operating expenses",
    fields: [
      { k: "vacPct", label: "Vacancy %", type: "pct" },
      { k: "otherInc", label: "Other income/yr" },
      { k: "taxes", label: "Taxes/yr" },
      { k: "insBase", label: "Insurance base/yr" },
      { k: "insStress", label: "Insurance stress/yr" },
      { k: "mgmtPct", label: "Management %", type: "pct" },
      { k: "repairs", label: "Repairs/yr" },
      { k: "utilities", label: "Utilities/yr" },
      { k: "admin", label: "Admin/yr" },
      { k: "reserves", label: "Reserves/yr" },
    ],
  },
  {
    title: "🏦 Cost & financing",
    fields: [
      { k: "purchase", label: "Purchase price" },
      { k: "rehab", label: "Rehab budget" },
      { k: "soft", label: "Soft/closing/carry" },
      { k: "bridgeLTC", label: "Bridge LTC %", type: "pct" },
      { k: "permRate", label: "Perm rate %", type: "pct", step: "0.01" },
      { k: "amort", label: "Amortization (yrs)" },
      { k: "exitCap", label: "Exit cap %", type: "pct", step: "0.01" },
      { k: "targetLTV", label: "Target LTV %", type: "pct" },
      { k: "dscrMin", label: "Min DSCR", step: "0.01" },
      { k: "rentGrow", label: "Rent growth %", type: "pct", step: "0.01" },
      { k: "expGrow", label: "Expense growth %", type: "pct", step: "0.01" },
    ],
  },
  {
    title: "🤝 Investors & waterfall",
    fields: [
      { k: "sponsorCo", label: "Sponsor co-invest" },
      { k: "lp1", label: "LP 1 amount" },
      { k: "lp2", label: "LP 2 amount" },
      { k: "lp3", label: "LP 3 amount" },
      { k: "prefPct", label: "Preferred return %", type: "pct", step: "0.01" },
      { k: "gpPromote", label: "GP promote %", type: "pct" },
    ],
  },
  {
    title: "📍 Market signals & quality",
    fields: [
      { k: "popGrow", label: "Pop growth 10yr %", type: "pct" },
      { k: "unemp", label: "Unemployment %", type: "pct", step: "0.001" },
      { k: "renterRising", label: "Renter share rising", type: "bool" },
      { k: "comps", label: "Comps strength 1-3" },
      { k: "concHigh", label: "Voucher over-concentration", type: "bool" },
      { k: "phaActive", label: "PHA active", type: "bool" },
      { k: "samClear", label: "Owner SAM-clear", type: "bool" },
      { k: "hasDeficiencies", label: "Known inspection issues", type: "bool" },
    ],
  },
];

export function DealInputs({
  deal,
  onChange,
  onDelete,
}: {
  deal: Deal;
  onChange: (patch: Partial<Deal>) => void;
  onDelete: () => void;
}) {
  const set = (k: keyof Deal, raw: string, type: FieldType) => {
    let v: string | number | boolean;
    if (type === "bool") v = raw === "1";
    else if (type === "text" || type === "select") v = raw;
    else {
      let n = parseFloat(raw);
      if (isNaN(n)) n = 0;
      v = type === "pct" ? n / 100 : n;
    }
    onChange({ [k]: v } as Partial<Deal>);
  };

  return (
    <div>
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>These are the only numbers you enter. Everything else in this deal file is calculated from them live. Try changing insurance or the purchase price and scroll down.</p>
      </div>
      {GROUPS.map((g) => (
        <details className="inp" key={g.title} open={g.open}>
          <summary>{g.title}</summary>
          <div className="body">
            <div className="inputs">
              {g.fields.map((f) => {
                const t = f.type ?? "num";
                const val = deal[f.k];
                return (
                  <div className="fld" key={String(f.k)}>
                    <label>{f.label}</label>
                    {t === "bool" ? (
                      <select value={val ? "1" : "0"} onChange={(e) => set(f.k, e.target.value, t)}>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    ) : t === "select" ? (
                      <select value={String(val)} onChange={(e) => set(f.k, e.target.value, t)}>
                        {f.opts!.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    ) : t === "text" ? (
                      <input type="text" value={String(val ?? "")} onChange={(e) => set(f.k, e.target.value, t)} />
                    ) : (
                      <input
                        type="number"
                        step={f.step ?? "any"}
                        value={t === "pct" ? (Number(val) * 100) : Number(val)}
                        onChange={(e) => set(f.k, e.target.value, t)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </details>
      ))}
      <button className="btn btn-g btn-sm" onClick={onDelete}>🗑 Delete this deal</button>
    </div>
  );
}
